<script setup>
/* global axios */
import { reactive, ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CampaignsAPI from 'dashboard/api/campaigns';
import TeletelMetaStatusAPI from 'dashboard/api/teletel/metaStatus';
import {
  buildTemplateParameters,
  replaceTemplateVariables,
  findComponentByType,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
} from 'dashboard/helper/templateHelper';
import {
  emptyDynamicFilter,
  dynamicFilterQuery,
  dynamicFilterEntry,
  dynamicFilterSummary,
  PERIODS,
} from 'dashboard/helper/campaignDynamicFilter';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppBubblePreview from './WhatsAppBubblePreview.vue';

// The campaign page in three steps (owner order 2026-09-25: «الأجمل
// والأفضل والأسهل»): message → audience → review. Same payload as the old
// dialog, same server guards; new here: a real file upload for the header
// media (permanent public address, so Meta can fetch it at send time), a
// file of numbers as an audience, and the Meta verdict before sending.
const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();
const store = useStore();
const route = useRoute();

const tr = (key, params) => t(`CAMPAIGN.WHATSAPP.WIZARD.${key}`, params);

const labels = useMapGetter('labels/getLabels');
const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);
const uiFlags = useMapGetter('campaigns/getUIFlags');
const lifecycleReady = useMapGetter('customerLifecycle/isReady');
const lifecycleStages = useMapGetter('customerLifecycle/getActiveStages');

const step = ref(1);
const state = reactive({
  title: '',
  inboxId: null,
  templateId: null,
  processedParams: {},
  mediaPreviewUrl: '',
  mediaFileName: '',
  selectedAudience: [],
  selectedStages: [],
  dynamic: emptyDynamicFilter(),
  when: 'now',
  scheduledAt: null,
});

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('customerLifecycle/getSettings').then(settings => {
    if (settings?.available && settings?.enabled)
      store.dispatch('customerLifecycle/getStages');
  });
});

// ---- step 1: the message
const inboxOptions = computed(() =>
  (inboxes.value || []).map(i => ({ value: i.id, label: i.name }))
);
const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  return getFilteredWhatsAppTemplates.value(state.inboxId).map(template => ({
    // A template without a Meta id (dev fixtures) still needs a stable key.
    value: template.id ?? `${template.name}:${template.language}`,
    label: `${template.name.replace(/_/g, ' ')} (${template.language || 'en'})`,
    template,
  }));
});
const selectedTemplate = computed(
  () =>
    templateOptions.value.find(o => o.value === state.templateId)?.template ||
    null
);
const selectedInbox = computed(() =>
  (inboxes.value || []).find(i => i.id === state.inboxId)
);
const headerComponent = computed(() =>
  selectedTemplate.value
    ? findComponentByType(selectedTemplate.value, COMPONENT_TYPES.HEADER)
    : null
);
const headerFormat = computed(() =>
  String(headerComponent.value?.format || '').toUpperCase()
);
const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerFormat.value)
);
const bodyVariables = computed(() =>
  Object.keys(state.processedParams.body || {})
);
const renderedTemplate = computed(() => {
  const body = selectedTemplate.value
    ? findComponentByType(selectedTemplate.value, COMPONENT_TYPES.BODY)
    : null;
  return body ? replaceTemplateVariables(body.text, state.processedParams) : '';
});

watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
  }
);
watch(selectedTemplate, template => {
  state.processedParams = template
    ? buildTemplateParameters(template, hasMediaHeader.value)
    : {};
  state.mediaPreviewUrl = '';
  state.mediaFileName = '';
});

const uploading = ref(false);
const mediaInput = ref(null);
const ACCEPT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4',
  DOCUMENT: 'application/pdf',
};
const mediaAccept = computed(() => ACCEPT[headerFormat.value] || '');

// The stock upload endpoint stores the file and returns a signed blob id;
// the permanent public address below is what Meta fetches when the
// campaign actually sends (the storage's own URL expires in minutes).
const uploadMedia = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  uploading.value = true;
  try {
    const form = new FormData();
    form.append('attachment', file);
    const { data } = await axios.post(
      `/api/v1/accounts/${route.params.accountId}/upload`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    state.processedParams.header ??= {};
    state.processedParams.header.media_url = `${window.location.origin}/teletel/campaign-media/${data.blob_id}`;
    state.processedParams.header.media_type = headerFormat.value.toLowerCase();
    if (headerFormat.value === 'DOCUMENT')
      state.processedParams.header.media_name = file.name;
    state.mediaPreviewUrl =
      headerFormat.value === 'IMAGE' ? URL.createObjectURL(file) : 'ready';
    state.mediaFileName = file.name;
  } catch (e) {
    useAlert(e?.response?.data?.error || tr('MESSAGE.MEDIA.ERROR'));
  } finally {
    uploading.value = false;
    if (mediaInput.value) mediaInput.value.value = '';
  }
};

const mediaMissing = computed(
  () => hasMediaHeader.value && !state.processedParams.header?.media_url
);
const variablesMissing = computed(() =>
  bodyVariables.value.some(k => !state.processedParams.body?.[k])
);
const step1Errors = computed(() => {
  const errors = [];
  if (!state.title.trim()) errors.push(tr('MESSAGE.ERRORS.TITLE'));
  if (!state.inboxId) errors.push(tr('MESSAGE.ERRORS.INBOX'));
  if (!state.templateId) errors.push(tr('MESSAGE.ERRORS.TEMPLATE'));
  if (variablesMissing.value) errors.push(tr('MESSAGE.ERRORS.VARIABLES'));
  if (mediaMissing.value) errors.push(tr('MESSAGE.ERRORS.MEDIA'));
  return errors;
});
const categoryNote = computed(() => {
  const category = selectedTemplate.value?.category;
  if (!category) return '';
  return t(
    `CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.CATEGORY_NOTE.${
      category === 'MARKETING' ? 'MARKETING' : 'UTILITY'
    }`
  );
});

// ---- step 2: the audience
const audienceTab = ref('labels');
const labelOptions = computed(() =>
  (labels.value || []).map(l => ({ value: l.id, label: l.title }))
);
const stageOptions = computed(() =>
  (lifecycleStages.value || []).map(s => ({
    value: s.id,
    label: `${s.emoji || ''} ${s.name}`.trim(),
  }))
);

const audience = ref(null);
const audienceLoading = ref(false);
let audienceRequest = 0;
// Dynamic filter (owner order 2026-09-29): who messaged first, within a
// period, capped at the newest N. Resolved by the server at send time.
const dynamicQuery = computed(() => dynamicFilterQuery(state.dynamic));
const dynamicSummary = computed(() =>
  dynamicFilterSummary(state.dynamic, (key, params) =>
    tr(`AUDIENCE.DYNAMIC.${key}`, params)
  )
);
const periodOptions = PERIODS;
const periodLabel = period =>
  tr(
    `AUDIENCE.DYNAMIC.PERIOD.${
      period === 'all' || period === 'custom'
        ? period.toUpperCase()
        : `DAYS_${period}`
    }`
  );

const fetchAudience = async () => {
  const ids = state.selectedAudience || [];
  const stageIds = state.selectedStages || [];
  const dynamic = dynamicQuery.value;
  if (!ids.length && !stageIds.length && !dynamic) {
    audience.value = null;
    return;
  }
  audienceRequest += 1;
  const seq = audienceRequest;
  audienceLoading.value = true;
  try {
    const { data } = await CampaignsAPI.audiencePreview(ids, stageIds, dynamic);
    if (seq === audienceRequest) audience.value = data.payload;
  } catch (e) {
    if (seq === audienceRequest) audience.value = null;
  } finally {
    if (seq === audienceRequest) audienceLoading.value = false;
  }
};
watch(
  () => [state.selectedAudience, state.selectedStages, dynamicQuery.value],
  fetchAudience,
  { deep: true }
);
const audienceEmpty = computed(
  () => audience.value !== null && audience.value.reachable === 0
);

const addLabelToAudience = async labelId => {
  await store.dispatch('labels/get');
  if (labelId && !state.selectedAudience.includes(labelId))
    state.selectedAudience = [...state.selectedAudience, labelId];
};

// Paste
const pasteNumbers = ref('');
const pasteLabel = ref('');
const pasting = ref(false);
const pasteResult = ref(null);
const pasteAudience = async () => {
  if (pasting.value || !pasteNumbers.value.trim() || !pasteLabel.value.trim())
    return;
  pasting.value = true;
  pasteResult.value = null;
  try {
    const { data } = await CampaignsAPI.pasteAudience(
      pasteNumbers.value,
      pasteLabel.value.trim()
    );
    pasteResult.value = data.payload;
    await addLabelToAudience(data.payload.label_id);
    pasteNumbers.value = '';
  } catch (e) {
    useAlert(e?.response?.data?.error || tr('AUDIENCE.PASTE.ERROR'));
  } finally {
    pasting.value = false;
  }
};

// File
const fileLabel = ref('');
const fileInput = ref(null);
const fileUploading = ref(false);
const fileResult = ref(null);
const uploadAudienceFile = async event => {
  const file = event.target.files?.[0];
  if (!file || !fileLabel.value.trim()) {
    if (file) useAlert(tr('AUDIENCE.FILE.LABEL_FIRST'));
    if (fileInput.value) fileInput.value.value = '';
    return;
  }
  fileUploading.value = true;
  fileResult.value = null;
  try {
    const { data } = await CampaignsAPI.uploadAudience(
      file,
      fileLabel.value.trim()
    );
    fileResult.value = data.payload;
    await addLabelToAudience(data.payload.label_id);
  } catch (e) {
    useAlert(e?.response?.data?.error || tr('AUDIENCE.FILE.ERROR'));
  } finally {
    fileUploading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};

const intakeSummary = result =>
  t('CAMPAIGN.WHATSAPP.CREATE.FORM.PASTE.RESULT', {
    created: result.created,
    updated: result.updated,
    duplicates: result.duplicates,
    invalid: result.invalid.length,
  });

const step2Errors = computed(() => {
  const errors = [];
  const hasStatic =
    state.selectedAudience.length || state.selectedStages.length;
  if (state.dynamic.enabled && !dynamicQuery.value && !hasStatic)
    errors.push(tr('AUDIENCE.DYNAMIC.ERRORS.EMPTY'));
  else if (!hasStatic && !dynamicQuery.value)
    errors.push(tr('AUDIENCE.ERRORS.EMPTY'));
  else if (audienceEmpty.value) errors.push(tr('AUDIENCE.ERRORS.UNREACHABLE'));
  return errors;
});

// ---- step 3: review
const metaNumber = ref(null);
const metaLoaded = ref(false);
const loadMeta = async () => {
  metaLoaded.value = false;
  try {
    const { data } = await TeletelMetaStatusAPI.show();
    metaNumber.value =
      (data.numbers || []).find(n => n.inbox_id === state.inboxId) || null;
  } catch (e) {
    metaNumber.value = null; // not an administrator, or not checked yet
  } finally {
    metaLoaded.value = true;
  }
};
const metaBlocked = computed(() => metaNumber.value?.can_send === 'BLOCKED');
const metaIssues = computed(() =>
  (metaNumber.value?.issues || []).filter(i => i.severity === 'blocked')
);

const currentDateTime = computed(() => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
});
const step3Errors = computed(() =>
  state.when === 'later' && !state.scheduledAt
    ? [tr('REVIEW.ERRORS.SCHEDULE')]
    : []
);

const scheduledAtUtc = () =>
  state.when === 'later'
    ? new Date(state.scheduledAt).toISOString()
    : new Date(Date.now() + 60 * 1000).toISOString();

const selectedLabelNames = computed(() =>
  labelOptions.value
    .filter(o => state.selectedAudience.includes(o.value))
    .map(o => o.label)
);
const selectedStageNames = computed(() =>
  stageOptions.value
    .filter(o => state.selectedStages.includes(o.value))
    .map(o => o.label)
);
const audienceChips = computed(() =>
  [
    ...selectedLabelNames.value,
    ...selectedStageNames.value,
    dynamicSummary.value,
  ].filter(Boolean)
);

// ---- navigation
const steps = ['message', 'audience', 'review'];
const errorsFor = n =>
  ({ 1: step1Errors.value, 2: step2Errors.value, 3: step3Errors.value })[n];
const attempted = reactive({ 1: false, 2: false, 3: false });
const next = () => {
  attempted[step.value] = true;
  if (errorsFor(step.value).length) return;
  step.value += 1;
  if (step.value === 3) loadMeta();
};
const back = () => {
  step.value = Math.max(1, step.value - 1);
};

const submitError = ref('');
const isCreating = computed(() => uiFlags.value.isCreating);
const submit = () => {
  attempted[3] = true;
  if (step3Errors.value.length) return;
  submitError.value = '';
  const template = selectedTemplate.value;
  emit(
    'submit',
    {
      title: state.title.trim(),
      message: renderedTemplate.value,
      template_params: {
        name: template.name,
        namespace: template.namespace || '',
        category: template.category || 'UTILITY',
        language: template.language || 'en_US',
        processed_params: state.processedParams,
      },
      inbox_id: state.inboxId,
      scheduled_at: scheduledAtUtc(),
      audience: [
        ...state.selectedAudience.map(id => ({ id, type: 'Label' })),
        ...state.selectedStages.map(id => ({ id, type: 'LifecycleStage' })),
        ...[dynamicFilterEntry(state.dynamic)].filter(Boolean),
      ],
    },
    {
      fail: message => {
        submitError.value = message;
      },
    }
  );
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- progress -->
    <ol class="flex items-center gap-2 text-sm">
      <li
        v-for="(name, i) in steps"
        :key="name"
        class="flex items-center gap-2"
        :class="i + 1 <= step ? 'text-n-slate-12' : 'text-n-slate-10'"
      >
        <span
          class="flex size-7 items-center justify-center rounded-full text-xs font-semibold"
          :class="
            i + 1 < step
              ? 'bg-n-teal-9 text-white'
              : i + 1 === step
                ? 'bg-n-brand text-white'
                : 'bg-n-slate-4 text-n-slate-11'
          "
        >
          <span v-if="i + 1 < step" class="i-lucide-check size-4" />
          <template v-else>{{ i + 1 }}</template>
        </span>
        <span :class="i + 1 === step ? 'font-semibold' : ''">
          {{ tr(`STEPS.${name.toUpperCase()}`) }}
        </span>
        <span
          v-if="i < steps.length - 1"
          class="mx-1 h-px w-8 bg-n-slate-6 sm:w-16"
        />
      </li>
    </ol>

    <div class="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px]">
      <!-- left: the step -->
      <div class="flex flex-col gap-5">
        <!-- step 1 -->
        <template v-if="step === 1">
          <Input
            v-model="state.title"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
            :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
          />
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
            </label>
            <ComboBox
              v-model="state.inboxId"
              :options="inboxOptions"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')
              "
              class="[&>div>button]:bg-n-alpha-black2"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
            </label>
            <ComboBox
              v-model="state.templateId"
              :options="templateOptions"
              :disabled="!state.inboxId"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')
              "
              class="[&>div>button]:bg-n-alpha-black2"
            />
            <p v-if="categoryNote" class="text-xs text-n-amber-11">
              {{ categoryNote }}
            </p>
          </div>

          <div
            v-if="hasMediaHeader"
            class="flex flex-col gap-2 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ tr(`MESSAGE.MEDIA.${headerFormat}`) }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ tr('MESSAGE.MEDIA.HINT') }}
            </p>
            <div class="flex flex-wrap items-center gap-2">
              <label
                class="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-n-brand px-3 py-1.5 text-sm font-medium text-white hover:brightness-110"
              >
                <span class="i-lucide-upload size-4" />
                {{
                  uploading
                    ? tr('MESSAGE.MEDIA.UPLOADING')
                    : tr('MESSAGE.MEDIA.CHOOSE')
                }}
                <input
                  ref="mediaInput"
                  type="file"
                  class="hidden"
                  :accept="mediaAccept"
                  :disabled="uploading"
                  @change="uploadMedia"
                />
              </label>
              <span
                v-if="state.mediaFileName"
                class="text-sm text-n-teal-11"
                dir="ltr"
              >
                {{ state.mediaFileName }}
              </span>
            </div>
          </div>

          <div v-if="bodyVariables.length" class="flex flex-col gap-2">
            <p class="text-sm font-medium text-n-slate-12">
              {{ tr('MESSAGE.VARIABLES') }}
            </p>
            <Input
              v-for="key in bodyVariables"
              :key="key"
              v-model="state.processedParams.body[key]"
              :label="`{{${key}}}`"
              :placeholder="tr('MESSAGE.VARIABLE_PLACEHOLDER')"
            />
          </div>
        </template>

        <!-- step 2 -->
        <template v-else-if="step === 2">
          <div class="flex gap-2 border-b border-n-weak">
            <button
              v-for="tab in ['labels', 'dynamic', 'paste', 'file']"
              :key="tab"
              type="button"
              class="-mb-px border-b-2 px-3 py-2 text-sm"
              :class="
                audienceTab === tab
                  ? 'border-n-brand font-medium text-n-slate-12'
                  : 'border-transparent text-n-slate-11'
              "
              @click="audienceTab = tab"
            >
              {{ tr(`AUDIENCE.TABS.${tab.toUpperCase()}`) }}
            </button>
          </div>

          <div v-if="audienceTab === 'labels'" class="flex flex-col gap-3">
            <TagMultiSelectComboBox
              v-model="state.selectedAudience"
              :options="labelOptions"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')
              "
              :message="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.HINT')"
              message-type="info"
              class="[&>div>button]:bg-n-alpha-black2"
            />
            <TagMultiSelectComboBox
              v-if="lifecycleReady"
              v-model="state.selectedStages"
              :options="stageOptions"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGES.LABEL')"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGES.PLACEHOLDER')
              "
              :message="t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGES.HINT')"
              message-type="info"
              class="[&>div>button]:bg-n-alpha-black2"
            />
          </div>

          <div
            v-else-if="audienceTab === 'dynamic'"
            class="flex flex-col gap-3"
          >
            <p class="text-xs text-n-slate-11">
              {{ tr('AUDIENCE.DYNAMIC.HINT') }}
            </p>
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <input
                v-model="state.dynamic.enabled"
                type="checkbox"
                class="mb-0"
              />
              {{ tr('AUDIENCE.DYNAMIC.ENABLE') }}
            </label>
            <div
              class="flex flex-col gap-3"
              :class="
                !state.dynamic.enabled ? 'pointer-events-none opacity-50' : ''
              "
            >
              <label class="flex flex-col gap-1 text-sm text-n-slate-12">
                {{ tr('AUDIENCE.DYNAMIC.SOURCE.LABEL') }}
                <select
                  v-model="state.dynamic.entrySource"
                  class="mb-0 text-sm"
                >
                  <option value="incoming_message">
                    {{ tr('AUDIENCE.DYNAMIC.SOURCE.INCOMING') }}
                  </option>
                  <option value="any">
                    {{ tr('AUDIENCE.DYNAMIC.SOURCE.ANY') }}
                  </option>
                </select>
              </label>
              <label class="flex flex-col gap-1 text-sm text-n-slate-12">
                {{ tr('AUDIENCE.DYNAMIC.PERIOD.LABEL') }}
                <select v-model="state.dynamic.period" class="mb-0 text-sm">
                  <option
                    v-for="period in periodOptions"
                    :key="period"
                    :value="period"
                  >
                    {{ periodLabel(period) }}
                  </option>
                </select>
              </label>
              <div
                v-if="state.dynamic.period === 'custom'"
                class="flex flex-col gap-2 sm:flex-row"
              >
                <label
                  class="flex flex-1 flex-col gap-1 text-sm text-n-slate-12"
                >
                  {{ tr('AUDIENCE.DYNAMIC.PERIOD.SINCE') }}
                  <input
                    v-model="state.dynamic.since"
                    type="date"
                    class="mb-0 text-sm"
                  />
                </label>
                <label
                  class="flex flex-1 flex-col gap-1 text-sm text-n-slate-12"
                >
                  {{ tr('AUDIENCE.DYNAMIC.PERIOD.UNTIL') }}
                  <input
                    v-model="state.dynamic.until"
                    type="date"
                    class="mb-0 text-sm"
                  />
                </label>
              </div>
              <label class="flex flex-col gap-1 text-sm text-n-slate-12">
                {{ tr('AUDIENCE.DYNAMIC.LIMIT.LABEL') }}
                <input
                  v-model="state.dynamic.limit"
                  type="number"
                  min="1"
                  step="1"
                  dir="ltr"
                  class="mb-0 text-sm"
                  :placeholder="tr('AUDIENCE.DYNAMIC.LIMIT.PLACEHOLDER')"
                />
                <span class="text-xs text-n-slate-11">
                  {{ tr('AUDIENCE.DYNAMIC.LIMIT.HINT') }}
                </span>
              </label>
            </div>
          </div>

          <div v-else-if="audienceTab === 'paste'" class="flex flex-col gap-2">
            <p class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.PASTE.HINT') }}
            </p>
            <textarea
              v-model="pasteNumbers"
              rows="6"
              dir="ltr"
              class="mb-0 font-mono text-sm"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.PASTE.PLACEHOLDER')
              "
            />
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                v-model="pasteLabel"
                type="text"
                class="mb-0 text-sm sm:flex-1"
                :placeholder="
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.PASTE.LABEL_PLACEHOLDER')
                "
              />
              <Button
                type="button"
                size="sm"
                :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.PASTE.SUBMIT')"
                :is-loading="pasting"
                :disabled="
                  pasting || !pasteNumbers.trim() || !pasteLabel.trim()
                "
                @click="pasteAudience"
              />
            </div>
            <p v-if="pasteResult" class="text-xs text-n-slate-12">
              {{ intakeSummary(pasteResult) }}
              <span
                v-if="pasteResult.invalid.length"
                dir="ltr"
                class="text-n-ruby-11"
              >
                {{ pasteResult.invalid.slice(0, 5).join(', ') }}
              </span>
            </p>
          </div>

          <div v-else class="flex flex-col gap-2">
            <p class="text-xs text-n-slate-11">
              {{ tr('AUDIENCE.FILE.HINT') }}
            </p>
            <input
              v-model="fileLabel"
              type="text"
              class="mb-0 text-sm"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.PASTE.LABEL_PLACEHOLDER')
              "
            />
            <label
              class="inline-flex w-fit cursor-pointer items-center gap-2 rounded-lg bg-n-brand px-3 py-1.5 text-sm font-medium text-white hover:brightness-110"
              :class="!fileLabel.trim() ? 'opacity-60' : ''"
            >
              <span class="i-lucide-file-up size-4" />
              {{
                fileUploading
                  ? tr('AUDIENCE.FILE.UPLOADING')
                  : tr('AUDIENCE.FILE.CHOOSE')
              }}
              <input
                ref="fileInput"
                type="file"
                class="hidden"
                accept=".csv,.txt,text/csv,text/plain"
                :disabled="fileUploading"
                @change="uploadAudienceFile"
              />
            </label>
            <p v-if="fileResult" class="text-xs text-n-slate-12">
              {{ intakeSummary(fileResult) }}
              <span
                v-if="fileResult.invalid.length"
                dir="ltr"
                class="text-n-ruby-11"
              >
                {{ fileResult.invalid.slice(0, 5).join(', ') }}
              </span>
            </p>
          </div>

          <!-- the counter -->
          <div
            class="rounded-lg border p-4"
            :class="
              audienceEmpty
                ? 'border-n-ruby-6 bg-n-ruby-2'
                : 'border-n-weak bg-n-alpha-1'
            "
          >
            <p v-if="audienceLoading" class="text-sm text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.COUNTING') }}
            </p>
            <p v-else-if="!audience" class="text-sm text-n-slate-11">
              {{ tr('AUDIENCE.COUNTER.EMPTY') }}
            </p>
            <div v-else class="flex flex-wrap items-baseline gap-x-6 gap-y-1">
              <p class="text-2xl font-semibold text-n-slate-12">
                {{ audience.reachable }}
                <span class="text-sm font-normal text-n-slate-11">
                  {{ tr('AUDIENCE.COUNTER.REACHABLE') }}
                </span>
              </p>
              <p class="text-sm text-n-slate-11">
                {{ tr('AUDIENCE.COUNTER.TOTAL', { count: audience.total }) }}
                ·
                {{
                  tr('AUDIENCE.COUNTER.NO_PHONE', {
                    count: audience.without_phone,
                  })
                }}
                ·
                {{
                  tr('AUDIENCE.COUNTER.OPTED_OUT', {
                    count: audience.opted_out,
                  })
                }}
                ·
                {{
                  tr('AUDIENCE.COUNTER.BLOCKED', { count: audience.blocked })
                }}
              </p>
            </div>
            <p v-if="audienceChips.length" class="mt-2 flex flex-wrap gap-1">
              <span
                v-for="name in audienceChips"
                :key="name"
                class="rounded-full bg-n-slate-4 px-2 py-0.5 text-xs text-n-slate-12"
              >
                {{ name }}
              </span>
            </p>
          </div>
        </template>

        <!-- step 3 -->
        <template v-else>
          <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-4 text-sm">
            <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
              <dt class="text-n-slate-11">{{ tr('REVIEW.TITLE') }}</dt>
              <dd class="font-medium text-n-slate-12">{{ state.title }}</dd>
              <dt class="text-n-slate-11">{{ tr('REVIEW.INBOX') }}</dt>
              <dd class="font-medium text-n-slate-12">
                {{ selectedInbox?.name }}
              </dd>
              <dt class="text-n-slate-11">{{ tr('REVIEW.TEMPLATE') }}</dt>
              <dd class="font-medium text-n-slate-12" dir="ltr">
                {{ selectedTemplate?.name }}
              </dd>
              <dt class="text-n-slate-11">{{ tr('REVIEW.AUDIENCE') }}</dt>
              <dd class="font-medium text-n-slate-12">
                {{
                  tr('AUDIENCE.COUNTER.TOTAL', {
                    count: audience?.reachable ?? 0,
                  })
                }}
                —
                {{ audienceChips.join('، ') }}
              </dd>
            </dl>
          </div>

          <div
            v-if="metaLoaded && metaNumber"
            class="rounded-lg border p-4 text-sm"
            :class="
              metaBlocked
                ? 'border-n-ruby-6 bg-n-ruby-2 text-n-ruby-12'
                : 'border-n-teal-6 bg-n-teal-2 text-n-teal-12'
            "
          >
            <p class="font-semibold">
              {{
                metaBlocked ? tr('REVIEW.META.BLOCKED') : tr('REVIEW.META.OK')
              }}
            </p>
            <ul v-if="metaIssues.length" class="mt-1 list-disc ps-5">
              <li v-for="issue in metaIssues" :key="issue.code">
                {{ issue.title || issue.meta_title }}
                <span v-if="issue.fix"> — {{ issue.fix }}</span>
              </li>
            </ul>
          </div>

          <div class="flex flex-col gap-3">
            <p class="text-sm font-medium text-n-slate-12">
              {{ tr('REVIEW.WHEN') }}
            </p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="w in ['now', 'later']"
                :key="w"
                type="button"
                class="rounded-lg border px-4 py-2 text-sm"
                :class="
                  state.when === w
                    ? 'border-n-brand bg-n-brand/10 font-medium text-n-slate-12'
                    : 'border-n-weak text-n-slate-11'
                "
                @click="state.when = w"
              >
                {{ tr(`REVIEW.${w.toUpperCase()}`) }}
              </button>
            </div>
            <Input
              v-if="state.when === 'later'"
              v-model="state.scheduledAt"
              type="datetime-local"
              :min="currentDateTime"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
            />
          </div>
          <p v-if="submitError" class="text-sm text-n-ruby-11">
            {{ submitError }}
          </p>
        </template>

        <!-- errors + nav -->
        <ul
          v-if="attempted[step] && errorsFor(step).length"
          class="list-disc rounded-lg bg-n-ruby-2 px-8 py-2 text-sm text-n-ruby-11"
        >
          <li v-for="e in errorsFor(step)" :key="e">{{ e }}</li>
        </ul>
        <div
          class="flex items-center justify-between gap-3 border-t border-n-weak pt-4"
        >
          <Button
            variant="faded"
            color="slate"
            type="button"
            :label="step === 1 ? tr('NAV.CANCEL') : tr('NAV.BACK')"
            @click="step === 1 ? emit('cancel') : back()"
          />
          <Button
            v-if="step < 3"
            type="button"
            :label="tr('NAV.NEXT')"
            icon="i-lucide-arrow-left"
            @click="next"
          />
          <Button
            v-else
            type="button"
            :label="state.when === 'now' ? tr('NAV.SEND') : tr('NAV.SCHEDULE')"
            icon="i-lucide-send"
            :is-loading="isCreating"
            :disabled="isCreating || (metaBlocked && state.when === 'now')"
            @click="submit"
          />
        </div>
      </div>

      <!-- right: the phone -->
      <aside class="lg:sticky lg:top-4 lg:self-start">
        <WhatsAppBubblePreview
          :template="selectedTemplate"
          :processed-params="state.processedParams"
          :media-preview-url="state.mediaPreviewUrl"
          :sender-name="selectedInbox?.name || ''"
        />
      </aside>
    </div>
  </div>
</template>
